import React from 'react';
import { Box, IconButton, Popover, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { apiGetDrawingSummary } from 'API/lotteryApi';
import { DrawingPrizeType, type IDrawingSummary } from 'types/IDrawingSummary';
import { formatCount, formatCurrencyEuro } from 'utility/formatCurrency';
import { prizeLabel } from 'utility/prizeLabel';
import { formatDate } from 'utility/formatDate';

import './DrawingSummaryPopover.scss';

interface IProps {
  drawingId: number | null;
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

export const DrawingSummaryPopover: React.FC<IProps> = ({ drawingId, anchorEl, onClose }) => {
  const [loaded, setLoaded] = React.useState<{ id: number; data: IDrawingSummary } | null>(null);

  React.useEffect(() => {
    if (drawingId === null) return;

    let current = true;
    apiGetDrawingSummary(drawingId)
      .then((data) => {
        if (current) setLoaded({ id: drawingId, data });
      })
      .catch(() => {
        if (current) setLoaded(null);
      });

    return () => {
      current = false;
    };
  }, [drawingId]);

  // The row under the pointer can change before the answer arrives, so a
  // summary fetched for an earlier row must not show under the new one.
  const summary = loaded && loaded.id === drawingId ? loaded.data : null;

  return (
    <Popover
      open={Boolean(anchorEl && drawingId !== null)}
      anchorEl={anchorEl}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      disableScrollLock
      slotProps={{ paper: { className: 'psf-drawing-summary' } }}
    >
      <IconButton className="psf-drawing-summary__close" onClick={onClose} size="small" aria-label="Schließen">
        <CloseIcon fontSize="small" />
      </IconButton>

      {!summary ? (
        <Typography className="psf-drawing-summary__loading">Wird geladen …</Typography>
      ) : (
        <>
          <Box className="psf-drawing-summary__head">
            <Typography>
              {summary.drawingTypeName}
              {summary.drawingDate ? ` vom ${formatDate(summary.drawingDate)}` : ''}
            </Typography>
            {summary.drawingDateNext && <Typography>Die nächste Ziehung findet statt am {formatDate(summary.drawingDateNext)}</Typography>}
            {summary.datePublish && <Typography>Die nächste Veröffentlichung findet statt am {formatDate(summary.datePublish)}</Typography>}
          </Box>

          {summary.finalDigits.length > 0 && (
            <table className="psf-drawing-summary__table psf-drawing-summary__table--no-border-bottom" style={{ width: '66.66%' }}>
              <thead>
                <tr>
                  <th>Endziffer(n)</th>
                  <th>Gewinn</th>
                </tr>
              </thead>
              <tbody>
                {summary.finalDigits.map((digit) => (
                  <tr key={`${digit.prizeId}-${digit.digits}`}>
                    <td>{digit.digits}</td>
                    <td>{prizeLabel(digit.typ, digit.value, digit.title)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {summary.prizeCounts.length > 0 && (
            <table className="psf-drawing-summary__table">
              <thead>
                <tr>
                  <th>Gewinnanzahl</th>
                  <th colSpan={2}>Gewinne zu</th>
                </tr>
              </thead>
              <tbody>
                {summary.prizeCounts.map((count) => {
                  // A non-cash prize has no amount to show, only its name.
                  const isCash = count.typ === DrawingPrizeType.GELDGEWINN;
                  return (
                    <tr key={count.prizeId}>
                      <td>{formatCount(count.times)}</td>
                      <td>{isCash ? formatCurrencyEuro(count.value) : (count.title ?? 'Keine Angabe')}</td>
                      <td>{isCash ? formatCurrencyEuro(count.total) : ''}</td>
                    </tr>
                  );
                })}
                <tr className="psf-drawing-summary__total-label">
                  <td colSpan={3}>zusammen:</td>
                </tr>
                <tr className="psf-drawing-summary__total">
                  <td>{formatCount(summary.totalCount)}</td>
                  <td>Gewinne über</td>
                  <td>{formatCurrencyEuro(summary.totalAmount)}</td>
                </tr>
              </tbody>
            </table>
          )}
        </>
      )}
    </Popover>
  );
};
